/**
 * Transcript materializer — the browser twin of `apply_transcript_frame` in
 * `crates/doc/src/transcript_delta.rs`.
 *
 * `WatchDocMessages` streams `TranscriptUpdate` frames: a full `reset` first,
 * then `upsert` / `append` / `remove` deltas carrying only what changed, with a
 * `count` tripwire on every delta. This module applies them in place and fails
 * loudly on any inconsistency — a diverged copy must resubscribe for a reset,
 * never render a plausible-looking wrong transcript.
 *
 * The rules are copied from Rust deliberately, including the order of
 * operations (removes, then upserts in frame order, then appends) and the
 * length check after each append:
 *
 * - `remove` drops entries by id;
 * - `upsert` removes any existing entry with that id, then inserts after the
 *   `after` anchor (`null` = head). A missing anchor is a desync — anchors are
 *   always prior upserts of the same frame or unchanged entries;
 * - `append` pushes text onto one text/reasoning part and verifies the resulting
 *   length equals `len` (UTF-16 code units, matching JS `String.length` and
 *   Rust's `str::len` for ASCII-heavy text; see the note below);
 * - `count` must match the resulting length.
 *
 * Note on `len`: Rust compares `String::len()` (UTF-8 *bytes*) while JS
 * `String.length` is UTF-16 code units. They agree for ASCII and disagree for
 * non-ASCII text. We therefore compare against `len` only when the appended
 * text is ASCII-only, and otherwise fall back to a monotonic-growth check. That
 * keeps the tripwire useful for the common case without false desyncs on
 * emoji/CJK transcripts.
 */

import type { MessagePart, TranscriptEntry, UnknownPart } from "./models";

export interface TranscriptUpsert {
  after: string | null;
  entry: TranscriptEntry;
}

export interface TextAppend {
  entry: string;
  part: string;
  text: string;
  len: number;
}

export type TranscriptFrame =
  | { reset: TranscriptEntry[]; historyPending?: boolean; contextUsage?: unknown }
  | {
      upsert?: TranscriptUpsert[];
      append?: TextAppend[];
      remove?: string[];
      count?: number;
      contextUsage?: unknown;
    };

export interface TranscriptState {
  entries: TranscriptEntry[];
  /** Host-owned context-window usage, when the engine reports it. */
  contextUsage: unknown;
  /** True while the opening-tail preview is showing and full history is still
   * loading (`historyPending` on the first frame of `openingTail:true`). */
  historyPending: boolean;
}

export class TranscriptDesyncError extends Error {
  constructor(message: string) {
    super(`transcript delta desync: ${message}`);
    this.name = "TranscriptDesyncError";
  }
}

export const emptyTranscript = (): TranscriptState => ({
  entries: [],
  contextUsage: null,
  historyPending: false
});

/** Normalize a part so an unknown kind still keeps its slot and id. */
export const normalizePart = (part: unknown): MessagePart => {
  if (typeof part !== "object" || part === null) {
    return { kind: "unknown", id: "" } as UnknownPart;
  }
  const raw = part as Record<string, unknown>;
  const kind = typeof raw.kind === "string" ? raw.kind : "unknown";
  const id = typeof raw.id === "string" ? raw.id : "";
  switch (kind) {
    case "text":
    case "reasoning":
      return { kind, id, text: typeof raw.text === "string" ? raw.text : "" } as MessagePart;
    case "tool":
      return {
        kind,
        id,
        call: (raw.call as ToolPartCall | null) ?? null,
        isError: raw.isError === true,
        resolved: raw.resolved === true,
        output: typeof raw.output === "string" ? raw.output : null,
        outputRef: typeof raw.outputRef === "string" ? raw.outputRef : null,
        outputBytes: typeof raw.outputBytes === "number" ? raw.outputBytes : null,
        diffRef: typeof raw.diffRef === "string" ? raw.diffRef : null,
        diffStats: Array.isArray(raw.diffStats) ? raw.diffStats : null,
        diff: Array.isArray(raw.diff) ? raw.diff : null,
        subagentRef: typeof raw.subagentRef === "string" ? raw.subagentRef : null,
        subagentStatus:
          raw.subagentStatus === "running" ||
          raw.subagentStatus === "done" ||
          raw.subagentStatus === "failed"
            ? raw.subagentStatus
            : null,
        subagentTail: typeof raw.subagentTail === "string" ? raw.subagentTail : null
      } as MessagePart;
    case "input":
      return {
        kind,
        id,
        requestId: typeof raw.requestId === "string" ? raw.requestId : "",
        questions: Array.isArray(raw.questions) ? raw.questions : [],
        resolved: raw.resolved === true
      } as MessagePart;
    case "error":
      return { kind, id, message: typeof raw.message === "string" ? raw.message : "" } as MessagePart;
    case "fork":
      return {
        kind,
        id,
        sourceChatId: typeof raw.sourceChatId === "string" ? raw.sourceChatId : "",
        sourceTitle: typeof raw.sourceTitle === "string" ? raw.sourceTitle : ""
      } as MessagePart;
    case "image":
      return {
        kind,
        id,
        path: typeof raw.path === "string" ? raw.path : "",
        name: typeof raw.name === "string" ? raw.name : "",
        mimeType: typeof raw.mimeType === "string" ? raw.mimeType : ""
      } as MessagePart;
    default:
      return { kind, id } as UnknownPart;
  }
};

type ToolPartCall = { name?: string; title?: string; args?: unknown } | null;

/** Normalize one entry off the wire. Tolerates missing optional fields. */
export const normalizeEntry = (entry: unknown): TranscriptEntry => {
  const raw = (typeof entry === "object" && entry !== null ? entry : {}) as Record<string, unknown>;
  const role = raw.role === "user" || raw.role === "system" ? raw.role : "assistant";
  return {
    id: typeof raw.id === "string" ? raw.id : "",
    role,
    parts: Array.isArray(raw.parts) ? raw.parts.map(normalizePart) : [],
    createdAt: typeof raw.createdAt === "number" ? raw.createdAt : 0,
    deviceId: typeof raw.deviceId === "string" ? raw.deviceId : "",
    status:
      raw.status === "streaming" || raw.status === "complete" || raw.status === "aborted"
        ? raw.status
        : null,
    continuationOf: typeof raw.continuationOf === "string" ? raw.continuationOf : null,
    durationMs: typeof raw.durationMs === "number" ? raw.durationMs : null
  };
};

const isAscii = (text: string): boolean => {
  for (let i = 0; i < text.length; i += 1) {
    if (text.charCodeAt(i) > 0x7f) return false;
  }
  return true;
};

/**
 * Apply one frame in place. Throws [`TranscriptDesyncError`] on any
 * inconsistency; the caller must resubscribe (a reset is the only repair).
 */
export const applyFrame = (state: TranscriptState, frame: TranscriptFrame): void => {
  if ("reset" in frame && Array.isArray(frame.reset)) {
    state.entries = frame.reset.map(normalizeEntry);
    state.historyPending = frame.historyPending === true;
    if ("contextUsage" in frame) state.contextUsage = frame.contextUsage ?? null;
    return;
  }
  const delta = frame as Exclude<TranscriptFrame, { reset: TranscriptEntry[] }>;
  state.historyPending = false;
  if ("contextUsage" in delta) state.contextUsage = delta.contextUsage ?? null;
  const entries = state.entries;

  for (const id of delta.remove ?? []) {
    const at = entries.findIndex((entry) => entry.id === id);
    if (at >= 0) entries.splice(at, 1);
  }

  for (const { after, entry } of delta.upsert ?? []) {
    const normalized = normalizeEntry(entry);
    const existing = entries.findIndex((candidate) => candidate.id === normalized.id);
    if (existing >= 0) entries.splice(existing, 1);
    let at: number;
    if (after === null || after === undefined) {
      at = 0;
    } else {
      const anchor = entries.findIndex((candidate) => candidate.id === after);
      if (anchor < 0) throw new TranscriptDesyncError(`missing anchor ${after}`);
      at = anchor + 1;
    }
    entries.splice(at, 0, normalized);
  }

  for (const { entry, part, text, len } of delta.append ?? []) {
    const target = entries.find((candidate) => candidate.id === entry);
    if (target === undefined) throw new TranscriptDesyncError(`missing append entry ${entry}`);
    const found = target.parts.find(
      (candidate) =>
        (candidate.kind === "text" || candidate.kind === "reasoning") &&
        (candidate as { id: string }).id === part
    ) as { text: string } | undefined;
    if (found === undefined) throw new TranscriptDesyncError(`missing append part ${part}`);
    const before = found.text.length;
    found.text += text;
    if (isAscii(text)) {
      // Byte length == code-unit length for ASCII, so Rust's tripwire applies.
      if (found.text.length !== len) {
        throw new TranscriptDesyncError(
          `append length mismatch on ${entry}#${part}: have ${found.text.length}, expected ${len}`
        );
      }
    } else if (found.text.length <= before) {
      throw new TranscriptDesyncError(`append did not grow ${entry}#${part}`);
    }
  }

  if (delta.count !== undefined && entries.length !== delta.count) {
    throw new TranscriptDesyncError(`count mismatch: have ${entries.length}, expected ${delta.count}`);
  }
};

/**
 * A viewport for one chat: owns the entry list plus the subscription lifecycle
 * bookkeeping the UI needs to resubscribe after a desync or reconnect.
 */
export class TranscriptView {
  state: TranscriptState = emptyTranscript();
  /** Bumped on every change so a renderer can cheaply detect staleness. */
  revision = 0;

  reset(): void {
    this.state = emptyTranscript();
    this.revision += 1;
  }

  /** Apply a raw stream item. Returns false when a resubscribe is required. */
  apply(item: unknown): boolean {
    try {
      applyFrame(this.state, item as TranscriptFrame);
      this.revision += 1;
      return true;
    } catch {
      return false;
    }
  }

  get entries(): readonly TranscriptEntry[] {
    return this.state.entries;
  }
}
